// Data contracts shared by the bake (tools/bake), the curated world state (data/world-state),
// the validator (tools/validate) and the browser. ARCHITECTURE.md §3 explains each file.
//
// Truth rule: every fact shown to a user traces to a `SourceId` that resolves in sources.json.

/** ISO 8601 calendar date, 'YYYY-MM-DD'. */
export type DateStr = string;

/** Key into sources.json. */
export type SourceId = string;

/** ISO 3166-1 alpha-3 for states ('USA'); lowercase slug for anything else ('rsf', 'houthis', 'nato'). */
export type ActorId = string;

// ─── Per-hex enumerations (values stored in *.grid.bin) ──────────────────────────────────────

export const TERRAIN = {
  OCEAN: 0,
  COAST: 1, // shallow water (shelf)
  LAKE: 2,
  GRASSLAND: 3,
  PLAINS: 4,
  DESERT: 5,
  TUNDRA: 6,
  SNOW: 7,
} as const;

export const FEATURE = {
  NONE: 0,
  WOODS: 1,
  RAINFOREST: 2,
  MARSH: 3,
  FLOODPLAINS: 4,
  ICE: 5,
  REEF: 6,
  OASIS: 7,
} as const;

export const HILL = { FLAT: 0, HILLS: 1, MOUNTAINS: 2 } as const;

export const RESOURCE = {
  NONE: 0,
  OIL: 1,
  GAS: 2,
  COAL: 3,
  URANIUM: 4,
  LITHIUM: 5,
  COBALT: 6,
  RARE_EARTHS: 7,
  COPPER: 8,
  IRON: 9,
  GOLD: 10,
  NICKEL: 11,
  GRAPHITE: 12,
} as const;

/** Grid fields and the file that owns each. Types: see gridfile.ts FIELD_TYPE. */
export const GRID_FIELDS = {
  // physical.grid.bin
  lat: 'f32', // hex centre latitude, degrees
  lon: 'f32', // hex centre longitude, degrees
  area_km2: 'f32', // true spherical area of the hex
  land: 'u8', // land fraction, 0..255
  elev: 'i16', // mean elevation in metres; negative = water depth
  elev_max: 'i16',
  relief: 'u16', // elevation standard deviation within the hex, metres
  terrain: 'u8', // TERRAIN
  feature: 'u8', // FEATURE
  hill: 'u8', // HILL
  river: 'u8', // bit d set = river along edge d (grid.ts DIR)
  river_size: 'u8', // 0 none, 1 river, 2 major river
  ice: 'u8', // glacier / ice-sheet / sea-ice fraction, 0..255

  // human.grid.bin
  owner: 'u16', // Country.id, de jure owner today under the default worldview; 0 = none
  admin1: 'u16', // Admin1.id; 0 = none
  citystate: 'u8', // 1 = hex forced so a state smaller than a hex still appears

  // sites.grid.bin
  pop: 'f32', // residents in the hex (GHSL 2025 epoch)
  city: 'u16', // City.id of the largest city in the hex; 0 = none
  resource: 'u8', // RESOURCE of the most significant geolocated deposit; 0 = none
  wonder: 'u16', // Wonder.id; 0 = none
} as const;

// ─── Baked JSON (public/data) ──────────────────────────────────────────────────────────────

export interface Source {
  id: SourceId;
  url: string;
  title: string;
  publisher: string;
  /** Full 'YYYY-MM-DD' only; omit it when the source gives just a month or a year. */
  published?: DateStr;
  accessed: DateStr;
  /** URLs this source itself cites (e.g. the AP story behind a Wikipedia line). */
  cites?: string[];
  license?: string;
  archive_url?: string;
}

/** countries.json — index 0 is reserved for "no owner", so ids start at 1. */
export interface Country {
  id: number;
  iso3: string;
  name: string;
  /** Natural Earth TYPE / our classification. */
  kind: 'sovereign' | 'territory' | 'disputed' | 'historical';
  un_member: boolean;
  /** Sub-hex state drawn as a Civ city-state. */
  city_state: boolean;
  colors: { primary: string; secondary: string };
  capital?: { name: string; lat: number; lon: number; hex: number };
  hexes: number;
  area_km2: number;
  sources: SourceId[];
}

export interface Admin1 {
  id: number;
  /** Natural Earth adm1_code, e.g. 'UKR-283'. Curated control geometry refers to these. */
  code: string;
  name: string;
  iso3: string;
}

export interface City {
  id: number;
  name: string;
  iso3: string;
  lat: number;
  lon: number;
  hex: number;
  pop: number;
  /** Year of `pop`; 0 = undated (GeoNames / Natural Earth give no year). */
  pop_year: number;
  /** Population by year where a dated series exists (e.g. GHSL epochs 2000, 2005, … 2025). */
  series?: Array<[year: number, pop: number]>;
  capital: boolean;
  sources: SourceId[];
}

export interface Wonder {
  id: number;
  name: string;
  iso3s: string[];
  lat: number;
  lon: number;
  hex: number;
  /** UNESCO World Heritage inscription year. */
  year: number;
  category: 'cultural' | 'natural' | 'mixed';
  danger: Array<{ from: number; to: number | null }>;
  url: string;
  sources: SourceId[];
}

export interface Deposit {
  hex: number;
  kind: number; // RESOURCE
  name?: string;
  lat: number;
  lon: number;
  sources: SourceId[];
}

/** resources.json */
export interface Resources {
  deposits: Deposit[];
  /** RESOURCE key ('OIL', 'RARE_EARTHS', …) → iso3 → national production/reserves (USGS MCS etc.).
   * iso3 'WLD' holds the world total; territories keep their own code ('GRL'). */
  stockpiles: Record<string, Record<string, { production?: number; reserves?: number; unit: string; year: number; sources: SourceId[] }>>;
}

/** pov.json — hexes whose owner differs from the default worldview, per point of view, grouped
 * by claim so the time slider can show when each claim began (e.g. Russia's 2022-09-30 claims). */
export type PovOverrides = Record<'usa' | 'chn' | 'rus', Array<{
  since: DateStr;
  to: DateStr | null;
  note: string;
  hexes: Array<[hex: number, countryId: number]>;
  sources: SourceId[];
}>>;

/** borders-history.json — de jure ownership changes after 2000-01-01, oldest first. */
export interface BorderChange {
  date: DateStr;
  /** Country.id before and after, per hex. Applying all changes to the 2000 base yields today. */
  hexes: Array<[hex: number, from: number, to: number]>;
  note: string;
  sources: SourceId[];
}

/** indicators.json */
export interface Indicators {
  meta: Record<string, { name: string; unit: string; sources: SourceId[]; license?: string; note?: string }>;
  /** indicator id → iso3 → [year, value] pairs, ascending year. */
  series: Record<string, Record<string, Array<[number, number]>>>;
}

/** milestones.json — dated, binary achievements (space race ladder, tech firsts). */
export interface Milestone {
  id: string;
  track: 'space' | 'tech';
  rung?: number;
  title: string;
  iso3: string;
  date: DateStr;
  sources: SourceId[];
}

/** events/<year>.json — one line per Wikipedia Current events bullet. */
export interface WorldEvent {
  d: DateStr;
  /** Portal heading, e.g. 'Armed conflicts and attacks'. */
  cat: string;
  /** Plain text of the bullet (Wikipedia, CC BY-SA 4.0). */
  t: string;
  /** Linked article titles. */
  links: string[];
  /** Countries the bullet is about. */
  c: string[];
  lat?: number;
  lon?: number;
  hex?: number;
  /** External citation URLs on the bullet. */
  refs: string[];
  /** How many leading `links` are the bullet's topic chain (Portal sub-headings); absent when 0. */
  topics?: number;
}

/** control/<theatre>.json — territorial control time series (e.g. DeepState for Ukraine). */
export interface ControlSeries {
  id: string;
  controller: ActorId;
  territory_of: string;
  sources: SourceId[];
  /** Snapshots ascending by date. `hexes` = [hex, fraction 0..255] of land under control.
   * `line` = front-line polylines, each flattened [lon0, lat0, lon1, lat1, …]. */
  snapshots: Array<{
    date: DateStr;
    source: SourceId;
    /** Every source behind the snapshot when there is more than `source` (e.g. a date source plus
     * the outline's source). */
    sources?: SourceId[];
    area_km2: number;
    hexes: Array<[number, number]>;
    line?: number[][];
  }>;
}

/** conflicts-ucdp.json — UCDP/PRIO armed conflicts per year since 2000 (CC BY 4.0). Final years
 * come from the Armed Conflict Dataset, the current year from the Candidate Events Dataset.
 * Producer: tools/bake/worldciv_bake/events_ucdp.py; field notes in docs/bake/events.md. */
export interface ConflictsUcdp {
  meta: {
    final: { dataset: string; years: [number, number]; sources: SourceId[] };
    candidate: { dataset: string; year: number; through: DateStr; sources: SourceId[] };
    gw_iso3_sources: SourceId[];
    license: string;
    intensity: Record<string, string>;
    /** Gleditsch-Ward state code → ISO3. */
    gw_iso3: Record<string, string>;
  };
  /** Sorted by id. */
  conflicts: UcdpConflict[];
}

export type UcdpConflictType = 'extrasystemic' | 'interstate' | 'intrastate' | 'internationalized intrastate';

export interface UcdpConflict {
  /** UCDP conflict_id. */
  id: number;
  name: string | null;
  /** null for a conflict known only from Candidate events. */
  incompatibility: 'territory' | 'government' | 'both' | null;
  territory: string | null;
  /** First battle-related death of the conflict. */
  start: DateStr | null;
  location: string | null;
  /** Type in the latest year that has one. */
  type: UcdpConflictType | null;
  last_year: number;
  /** Ascending; only years with at least 25 battle-related deaths. */
  years: UcdpYear[];
  sources: SourceId[];
}

export interface UcdpYear {
  y: number;
  status: 'final' | 'candidate';
  /** Candidate years: 'interstate' when both sides are states, else null. */
  type: UcdpConflictType | null;
  /** 1 = 25–999 battle-related deaths in the year, 2 = at least 1,000. */
  int: number;
  /** Side A states (ISO3); side A is always a government. */
  a: string[];
  /** States supporting side A with troops; null = not coded (candidate years). */
  a2: string[] | null;
  /** Side B states (ISO3), interstate conflicts only. */
  b: string[];
  /** Side B non-state actors, by UCDP name ('IS', 'Hezbollah'). */
  b_ns: string[];
  b2: string[] | null;
  /** ACD location: the governments in the dispute, NOT where the fighting was; null in candidate years. */
  loc: string[] | null;
  /** States where the year's events took place, most deaths first. */
  ev_loc: string[];
  /** This episode reached 25 deaths. */
  ep_start: DateStr | null;
  /** Only when the episode ended that year. */
  ep_end: DateStr | null;
  /** Sum of UCDP 'best' death estimates over the year's events. */
  deaths_best: number | null;
  /** Candidate years only: intensity counting events still under review. */
  int_best?: number;
  /** Candidate years only: deaths_best over events coded 'Clear' (these drive `int`). */
  deaths_clear?: number;
}

// ─── Curated world state (data/world-state → public/data/world.json) ──────────────────────

export type Confidence = 'high' | 'medium' | 'low';

/** Truth-rule 6 label shown beside a value (ARCHITECTURE §1). */
export type ClaimLabel = 'data' | 'official' | 'assessment' | 'unverified';

/** A dated fact. */
export interface Claim<T> {
  value: T;
  as_of: DateStr;
  sources: SourceId[];
  confidence: Confidence;
  /** Kind of claim; the UI falls back to 'assessment' when absent. */
  label?: ClaimLabel;
}

/** Something true between two dates. `to: null` = still true on the as-of date. */
export interface Span {
  from: DateStr;
  to: DateStr | null;
  sources: SourceId[];
  note?: string;
}

export interface Actor {
  id: ActorId;
  kind: 'state' | 'nonstate' | 'bloc' | 'historical-state';
  name: string;
  /** For non-state actors: the state where they operate. */
  based_in?: string;
  colors?: { primary: string; secondary: string };
  wiki?: string;
  sources: SourceId[];
}

export interface Bloc {
  id: ActorId;
  name: string;
  kind: 'military-alliance' | 'economic' | 'political' | 'forum';
  mutual_defense: boolean;
  color: string;
  sources: SourceId[];
}

export interface Membership extends Span {
  bloc: ActorId;
  member: ActorId;
  status: 'member' | 'suspended' | 'partner' | 'observer' | 'frozen';
}

/** Bilateral or plurilateral defence commitments outside blocs (e.g. US–Japan 1960). */
export interface Treaty extends Span {
  id: string;
  name: string;
  parties: ActorId[];
  kind: 'mutual-defense' | 'strategic-partnership' | 'union-state';
}

/** A sanctions programme or formal hostility list. Drives "Denounced". */
export interface Sanction extends Span {
  by: ActorId;
  target: ActorId;
  program: string;
  scope: 'comprehensive' | 'sectoral' | 'targeted' | 'unfriendly-list' | 'tariff';
}

/** When one party of a war took part: `from` inclusive, `to` exclusive; null = the war's own start or end. */
export interface PartySpan {
  actor: ActorId;
  from: DateStr | null;
  to: DateStr | null;
  sources: SourceId[];
  note?: string;
}

export interface War extends Span {
  id: string;
  name: string;
  wiki?: string;
  kind: 'interstate' | 'civil' | 'insurgency' | 'intervention' | 'border-clash';
  sides: { a: ActorId[]; b: ActorId[] };
  /** States whose territory hosts fighting. */
  theatre: string[];
  status: Claim<'active' | 'ceasefire' | 'frozen' | 'ended'>;
  intensity: Claim<'major' | 'minor'>;
  /** Short dated key developments. */
  events: Array<{ date: DateStr; text: string; sources: SourceId[] }>;
  /** Dates of the parties that joined late or left early; a party with no span took part for the whole war. */
  party_spans?: PartySpan[];
}

/** Where a controller holds land that belongs to another state. */
export type GeometryRef =
  | { admin1: string[]; partial?: string[] }
  | { file: string }
  | { series: string }
  | { points: Array<{ lat: number; lon: number; radius_km: number }> };

export interface ControlEpisode extends Span {
  id: string;
  war?: string;
  controller: ActorId;
  territory_of: string;
  status: 'occupied' | 'contested' | 'annexation-claimed' | 'breakaway';
  geometry: GeometryRef;
  confidence: Confidence;
}

export interface LeaderTerm extends Span {
  country: string;
  role: 'head_of_state' | 'head_of_government' | 'paramount_leader';
  name: string;
  /** Wikidata QID when known. */
  qid?: string;
}

export type LeaderId = 'trump' | 'xi' | 'putin';

export interface AgendaItem {
  title: string;
  /** Verbatim text, never paraphrased. */
  quote: string;
  speaker: string;
  document: string;
  date: DateStr;
  sources: SourceId[];
}

export interface Perspective {
  id: LeaderId;
  name: string;
  title: string;
  iso3: string;
  pov: 'usa' | 'chn' | 'rus';
  term: Span;
  agenda: AgendaItem[];
}

/** An official statement on a war, for the both-sides panel. */
export interface Statement {
  war: string;
  actor: ActorId;
  speaker: string;
  date: DateStr;
  quote: string;
  venue: string;
  sources: SourceId[];
}

/** world.json, compiled and validated from data/world-state/*.json. */
export interface WorldState {
  as_of: DateStr;
  actors: Actor[];
  blocs: Bloc[];
  memberships: Membership[];
  treaties: Treaty[];
  sanctions: Sanction[];
  wars: War[];
  control: ControlEpisode[];
  leaders: LeaderTerm[];
  perspectives: Perspective[];
  statements: Statement[];
  unverified: Array<{ claim: string; seen_in: string[]; note?: string }>;
}

/** seaice.bin — monthly sea-ice concentration on the hexes that ever have ice (NSIDC).
 *
 *   offset 0  magic "WCSI" · u16 version = 1 · u16 months · u32 hexCount · 8 bytes first month
 *             "YYYY-MM\0"
 *   then      u32 hex ids × hexCount (ascending)
 *   then      u8 concentration 0..255 × hexCount, one block per month, oldest first
 *
 * Month m is first month + m. Dates before the first month have no data; dates after the last
 * month use the last month. The bake's source registry cites every monthly file. */
export const SEA_ICE_MAGIC = 'WCSI';

